import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/context/AuthContext';
import { employeeService } from '../../admin/services/employeeService';
import { getContacts } from '../../contacts';
import { useToast } from '../../notifications';
import { createConversationTask, createConversationTaskFromMessage } from '../services/conversationService';
import { Message, ConversationParticipantRef } from '../types/Conversation';
import { CreateTaskRequest, TaskPriority } from '../../tasks/types/Task';
import { truncateMessageSnippet } from '../utils/messageUtils';
import detailsStyles from './ConversationDetails.module.css';
import { LocalizedDateInput } from '../../../shared/components';

const taskPriorityOptions: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

interface ConversationCreateTaskModalProps {
  conversationId: string;
  sourceMessage: Message | null;
  conversationDisplayName: string;
  getParticipantDisplayName: (participant: ConversationParticipantRef) => string;
  initialTitle: string;
  initialDescription: string;
  onClose: () => void;
  onCreated: () => void;
}

const ConversationCreateTaskModal: React.FC<ConversationCreateTaskModalProps> = ({
  conversationId,
  sourceMessage,
  conversationDisplayName,
  getParticipantDisplayName,
  initialTitle,
  initialDescription,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation(['conversations', 'common']);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { auth } = useAuth();

  const [title, setTitle] = React.useState(initialTitle);
  const [description, setDescription] = React.useState(initialDescription);
  const [dueDate, setDueDate] = React.useState('');
  const [priority, setPriority] = React.useState<TaskPriority>('medium');
  const [assigneeId, setAssigneeId] = React.useState(() => {
    return auth?.employeeId || '';
  });
  const [contactId, setContactId] = React.useState('');
  const [employeeOptions, setEmployeeOptions] = React.useState<Array<{ id: string; name: string }>>([]);
  const [contactOptions, setContactOptions] = React.useState<Array<{ id: string; name: string }>>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    void Promise.all([
      employeeService.getEmployees({ isActive: true, sortBy: 'name', sortDir: 'asc', pageSize: 200 }),
      getContacts(),
    ]).then(([employees, contacts]) => {
      setEmployeeOptions(employees.map(e => ({ id: e.id, name: e.name || e.email })));
      setContactOptions(contacts.map(c => ({ id: c.id, name: c.name })));
    }).catch(() => {
      // silently ignore; dropdowns will be empty
    });
  }, []);

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError(t('taskTitleRequired', { ns: 'conversations' }));
      return;
    }

    const request: CreateTaskRequest = {
      title: trimmedTitle,
      description: description.trim() || undefined,
      dueDate: dueDate || undefined,
      recurrence: 'doesNotRepeat',
      priority,
      assignedToUserId: assigneeId || undefined,
      contactId: contactId || undefined,
    };

    setSaving(true);
    setError(null);

    try {
      const task = sourceMessage
        ? await createConversationTaskFromMessage(conversationId, sourceMessage.id, request)
        : await createConversationTask(conversationId, request);

      if (!task) {
        throw new Error(t('failedToCreateTask', { ns: 'conversations' }));
      }

      onCreated();
      showToast(t('taskCreatedSuccessfully', { ns: 'conversations' }), 'success');
      navigate(`/tasks/${task.id}`);
    } catch {
      setError(t('failedToCreateTask', { ns: 'conversations' }));
      showToast(t('failedToCreateTask', { ns: 'conversations' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={detailsStyles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('createTaskModalTitle', { ns: 'conversations' })}>
      <div className={`${detailsStyles.modalCard} ${detailsStyles.taskModalCard}`}>
        <div className={detailsStyles.modalHeader}>
          <div className={detailsStyles.modalTitle}>{t('createTaskModalTitle', { ns: 'conversations' })}</div>
          <button
            type="button"
            className={detailsStyles.modalCloseBtn}
            onClick={onClose}
            disabled={saving}
          >
            {t('close', { ns: 'common' })}
          </button>
        </div>

        <div className={detailsStyles.modalList}>
          <div className={detailsStyles.taskForm}>
            <div className={detailsStyles.taskField}>
              <label className={detailsStyles.taskLabel} htmlFor="conversation-task-title">
                {t('taskTitleLabel', { ns: 'conversations' })}
              </label>
              <input
                id="conversation-task-title"
                type="text"
                value={title}
                onChange={event => setTitle(event.target.value)}
                className={detailsStyles.taskInput}
                placeholder={t('taskTitlePlaceholder', { ns: 'conversations' })}
                disabled={saving}
              />
            </div>

            <div className={detailsStyles.taskField}>
              <label className={detailsStyles.taskLabel} htmlFor="conversation-task-description">
                {t('taskDescriptionLabel', { ns: 'conversations' })}
              </label>
              <textarea
                id="conversation-task-description"
                value={description}
                onChange={event => setDescription(event.target.value)}
                className={detailsStyles.taskTextarea}
                placeholder={t('taskDescriptionPlaceholder', { ns: 'conversations' })}
                rows={4}
                disabled={saving}
              />
            </div>

            <div className={detailsStyles.taskFormGrid}>
              <div className={detailsStyles.taskField}>
                <label className={detailsStyles.taskLabel} htmlFor="conversation-task-priority">
                  {t('taskPriorityLabel', { ns: 'conversations' })}
                </label>
                <select
                  id="conversation-task-priority"
                  value={priority}
                  onChange={event => setPriority(event.target.value as TaskPriority)}
                  className={detailsStyles.taskInput}
                  disabled={saving}
                >
                  {taskPriorityOptions.map(p => (
                    <option key={p} value={p}>
                      {t(`taskPriority.${p}`, { ns: 'conversations' })}
                    </option>
                  ))}
                </select>
              </div>

              <div className={detailsStyles.taskField}>
                <label className={detailsStyles.taskLabel} htmlFor="conversation-task-due-date">
                  {t('taskDueDateLabel', { ns: 'conversations' })}
                </label>
                <LocalizedDateInput
                  id="conversation-task-due-date"
                  className={detailsStyles.taskInput}
                  value={dueDate}
                  onValueChange={setDueDate}
                  disabled={saving}
                  fillWidth
                />
              </div>
            </div>

            <div className={detailsStyles.taskFormGrid}>
              <div className={detailsStyles.taskField}>
                <label className={detailsStyles.taskLabel} htmlFor="conversation-task-assignee">
                  {t('taskAssigneeLabel', { ns: 'conversations' })}
                </label>
                <select
                  id="conversation-task-assignee"
                  value={assigneeId}
                  onChange={event => setAssigneeId(event.target.value)}
                  className={detailsStyles.taskInput}
                  disabled={saving}
                >
                  <option value="">{t('taskAssigneeUnassigned', { ns: 'conversations' })}</option>
                  {employeeOptions.map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={detailsStyles.taskField}>
                <label className={detailsStyles.taskLabel} htmlFor="conversation-task-contact">
                  {t('taskContactLabel', { ns: 'conversations' })}
                </label>
                <select
                  id="conversation-task-contact"
                  value={contactId}
                  onChange={event => setContactId(event.target.value)}
                  className={detailsStyles.taskInput}
                  disabled={saving}
                >
                  <option value="">{t('taskContactNone', { ns: 'conversations' })}</option>
                  {contactOptions.map(contact => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={detailsStyles.taskContextCard}>
              <div className={detailsStyles.taskContextLabel}>
                {sourceMessage
                  ? t('taskSourceMessageLabel', { ns: 'conversations' })
                  : t('taskSourceConversationLabel', { ns: 'conversations' })}
              </div>
              <div className={detailsStyles.taskContextBody}>
                {sourceMessage ? (
                  <>
                    <strong>{getParticipantDisplayName(sourceMessage.sender)}</strong>
                    <span>{truncateMessageSnippet(sourceMessage.content || t('noContent', { ns: 'conversations' }), 180)}</span>
                  </>
                ) : (
                  <span>{conversationDisplayName}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {error ? <div className={detailsStyles.modalError}>{error}</div> : null}

        <div className={detailsStyles.modalActions}>
          <button
            type="button"
            className={detailsStyles.modalPrimaryBtn}
            onClick={() => void handleSubmit()}
            disabled={saving}
          >
            {saving ? t('creatingTask', { ns: 'conversations' }) : t('createTaskAction', { ns: 'conversations' })}
          </button>
          <button
            type="button"
            className={detailsStyles.modalSecondaryBtn}
            onClick={onClose}
            disabled={saving}
          >
            {t('cancel', { ns: 'common' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversationCreateTaskModal;
